import React, { useCallback, useEffect, useRef, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";

import PageLayout from "../../components/PageLayout";
import Toast from "../../components/Toast";
import {
  GET_ASSAY_LIST_URL,
  GET_ASSAY_SEARCH_URL,
  POST_ASSAY_REVIEW_URL,
  POST_ASSAY_WEIGHT_URL,
  getData,
  postData,
} from "../../utils/api";
import { canAccessAssayInsights, getLoginDetails } from "../../utils/storage";
import InsightsHeaderIcon from "../../icons/newicons/insights.svg";
import { InsightsSubNav } from "./InsightsSubNav";

import "./insights.css";
import "./assay-insights.css";

let SEARCH_TIMER = null;
const PAGE_SIZE = 10;

const PRICE_OPTIONS = [
  { value: "pending", label: "Pending", short: "Pend" },
  { value: "look_ok", label: "Look OK", short: "OK" },
  { value: "low", label: "Low", short: "Low" },
  { value: "lower", label: "Lower", short: "Lower" },
  { value: "higher", label: "Higher", short: "High" },
  { value: "rejected", label: "Rejected", short: "Rej" },
];

const STATUS_FILTERS = PRICE_OPTIONS;

const EMPTY_TOTALS = {
  pending: 0,
  look_ok: 0,
  low: 0,
  lower: 0,
  higher: 0,
  rejected: 0,
};

function feedbackOf(item) {
  if (!item) return "pending";
  if (item.decision === "reject" || item.priceFeedback === "rejected") return "rejected";
  const fb = (item.priceFeedback || "").trim();
  if (!fb || fb === "pending") return "pending";
  return fb;
}

function money(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

function num(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", { maximumFractionDigits: 3 });
}

function itemKey(item, idx = 0) {
  return `${item?.productId || ""}::${item?.oem || ""}::${idx}`;
}

function rowId(item) {
  return `${item?.productId || ""}::${item?.oem || ""}`;
}

function isSameItem(a, b) {
  if (!a || !b) return false;
  if (a.productId && b.productId) return a.productId === b.productId;
  return (a.oem || "").toLowerCase() === (b.oem || "").toLowerCase();
}

const AssayInsights = () => {
  const canView = canAccessAssayInsights(getLoginDetails());
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [listQuery, setListQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const [statusFilter, setStatusFilter] = useState("pending");
  const [oemList, setOemList] = useState([]);
  const [listTotals, setListTotals] = useState(EMPTY_TOTALS);
  const [listTotal, setListTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [rowFeedback, setRowFeedback] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [editingWeightKey, setEditingWeightKey] = useState("");
  const [weightDraft, setWeightDraft] = useState("");
  const [savingWeightKey, setSavingWeightKey] = useState("");
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: 0 });

  useEffect(() => {
    if (!canView) {
      window.location.href = "/";
    }
  }, [canView]);

  const mergeRowFeedback = (list, append) => {
    setRowFeedback((prev) => {
      const next = append ? { ...prev } : {};
      list.forEach((item) => {
        next[rowId(item)] = feedbackOf(item);
      });
      return next;
    });
  };

  const loadList = useCallback(async ({ filter, offset = 0, append = false, text = "" }) => {
    try {
      if (append) setIsLoadingMore(true);
      else setIsListLoading(true);

      const params = {
        filter,
        count: PAGE_SIZE,
        offset,
      };
      const q = (text || "").trim();
      if (q) params.text = q;

      const data = await getData(GET_ASSAY_LIST_URL, params);
      const payload = data?.data || data || {};
      const list = payload.list || [];
      const totals = payload.totals || {};

      setOemList((prev) => (append ? [...prev, ...list] : list));
      mergeRowFeedback(list, append);
      setHasMore(Boolean(payload.areMoreItemsAvailable));
      setListTotal(Number(payload.total) || 0);
      setListTotals({
        pending: Number(totals.pending) || 0,
        look_ok: Number(totals.look_ok) || 0,
        low: Number(totals.low) || 0,
        lower: Number(totals.lower) || 0,
        higher: Number(totals.higher) || 0,
        rejected: Number(totals.rejected) || 0,
      });
    } catch (e) {
      if (!append) {
        setOemList([]);
        setRowFeedback({});
      }
      setListTotals(EMPTY_TOTALS);
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Failed to load OEM list",
        id: Date.now(),
      });
    } finally {
      setIsListLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) return;
    loadList({ filter: statusFilter, offset: 0, append: false, text: listQuery });
  }, [canView, statusFilter, listQuery, loadList]);

  const runSearch = useCallback(async (text) => {
    const q = (text || "").trim();
    if (q.length < 1) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }
    try {
      setIsSearching(true);
      const data = await getData(GET_ASSAY_SEARCH_URL, { text: q, count: 15 });
      const list = data?.list || data?.data?.list || [];
      setSuggestions(list);
      setActiveIndex(-1);
    } catch (e) {
      setSuggestions([]);
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Search failed",
        id: Date.now(),
      });
    } finally {
      setIsSearching(false);
    }
  }, []);

  const applySearchOem = (item) => {
    const oem = item?.oem || "";
    setQuery(oem);
    setListQuery(oem);
    setSuggestions([]);
    setFocused(false);
  };

  const onQueryChange = (value) => {
    setQuery(value);
    if (SEARCH_TIMER) clearTimeout(SEARCH_TIMER);
    SEARCH_TIMER = setTimeout(() => {
      const next = (value || "").trim();
      setListQuery(next);
      runSearch(value);
    }, 280);
  };

  const clearSearch = () => {
    if (SEARCH_TIMER) clearTimeout(SEARCH_TIMER);
    setQuery("");
    setListQuery("");
    setSuggestions([]);
    inputRef.current && inputRef.current.focus();
  };

  const setFeedbackForRow = (item, value) => {
    setRowFeedback((prev) => ({
      ...prev,
      [rowId(item)]: value,
    }));
  };

  const startEditWeight = (item) => {
    const key = rowId(item);
    setEditingWeightKey(key);
    setWeightDraft(item.subweight != null && item.subweight !== "" ? String(item.subweight) : "");
  };

  const cancelEditWeight = () => {
    setEditingWeightKey("");
    setWeightDraft("");
  };

  const saveWeight = async (item) => {
    if (!item || savingWeightKey) return;
    const key = rowId(item);
    const parsed = Number(weightDraft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setToastParams({
        type: "ERROR",
        message: "Enter a valid weight (≥ 0)",
        id: Date.now(),
      });
      return;
    }

    try {
      setSavingWeightKey(key);
      const data = await postData(POST_ASSAY_WEIGHT_URL, {
        productId: item.productId || "",
        oem: item.oem || "",
        weight: parsed,
      });
      const payload = data?.item || data?.data?.item || data;
      setOemList((prev) =>
        prev.map((row) => {
          if (!isSameItem(row, item)) return row;
          return {
            ...row,
            ...(payload && typeof payload === "object" ? payload : {}),
            subweight: payload?.subweight != null ? payload.subweight : parsed,
            assayPrice: payload?.assayPrice != null ? payload.assayPrice : row.assayPrice,
          };
        })
      );
      setEditingWeightKey("");
      setWeightDraft("");
      setToastParams({
        type: "SUCCESS",
        message: "Weight saved",
        id: Date.now(),
      });
    } catch (e) {
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Failed to save weight",
        id: Date.now(),
      });
    } finally {
      setSavingWeightKey("");
    }
  };

  const saveReview = async (item) => {
    if (!item) return;
    const key = rowId(item);
    const priceFeedback = rowFeedback[key] || feedbackOf(item);
    if (!priceFeedback || savingKey) return;

    try {
      setSavingKey(key);
      const data = await postData(POST_ASSAY_REVIEW_URL, {
        productId: item.productId || "",
        oem: item.oem || "",
        priceFeedback,
      });
      const payload = data?.item || data?.data?.item || data;
      const savedFeedback = feedbackOf({
        ...payload,
        priceFeedback: payload?.priceFeedback || priceFeedback,
      });
      const isRejected = savedFeedback === "rejected";
      const isPending = savedFeedback === "pending";
      const rowStatus = isRejected ? "rejected" : isPending ? "pending" : "complete";

      setRowFeedback((prev) => ({ ...prev, [key]: savedFeedback }));

      setOemList((prev) => {
        const next = prev.map((row) => {
          if (!isSameItem(row, item)) return row;
          return {
            ...row,
            ...(payload && payload.oem ? payload : {}),
            priceFeedback: savedFeedback === "pending" ? "" : savedFeedback,
            isComplete: !isRejected && !isPending,
            isRejected,
            status: rowStatus,
          };
        });
        return next.filter((row) => {
          if (!isSameItem(row, item)) return true;
          return feedbackOf(row) === statusFilter;
        });
      });

      setListTotals((prev) => {
        const next = { ...prev };
        const oldFeedback = feedbackOf(item);
        if (oldFeedback !== savedFeedback) {
          if (next[oldFeedback] != null) {
            next[oldFeedback] = Math.max(0, (prev[oldFeedback] || 0) - 1);
          }
          if (next[savedFeedback] != null) {
            next[savedFeedback] = (prev[savedFeedback] || 0) + 1;
          }
        }
        return next;
      });

      setToastParams({
        type: "SUCCESS",
        message: "Review saved",
        id: Date.now(),
      });
    } catch (e) {
      setToastParams({
        type: "ERROR",
        message: e && e.error ? e.error : "Failed to save review",
        id: Date.now(),
      });
    } finally {
      setSavingKey("");
    }
  };

  const onKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      applySearchOem(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setFocused(false);
    }
  };

  const onFilterChange = (nextFilter) => {
    if (nextFilter === statusFilter) return;
    setStatusFilter(nextFilter);
  };

  if (!canView) return null;

  const showDropdown = focused && (isSearching || query.trim().length > 0);

  return (
    <PageLayout icon={InsightsHeaderIcon} heading="Insights" searchPlaceholder="" hideSearchBar>
      <div className="assay-page">
        <InsightsSubNav />

        <div className="assay-search-wrap">
          <div className="assay-top-row">
            <div className="assay-search-panel-wrap">
              <OutsideClickHandler
                onOutsideClick={() => {
                  setFocused(false);
                }}
              >
                <div className="assay-search-panel">
                  <div className={`assay-search-box${focused ? " is-focused" : ""}`}>
                    <svg
                      className="assay-search-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M16.5 16.5 21 21"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    <input
                      ref={inputRef}
                      className="assay-search-input"
                      type="text"
                      value={query}
                      placeholder="Search OEM or description…"
                      onChange={(e) => onQueryChange(e.target.value)}
                      onFocus={() => {
                        setFocused(true);
                        if (query.trim() && !suggestions.length) runSearch(query);
                      }}
                      onKeyDown={onKeyDown}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {query ? (
                      <button
                        type="button"
                        className="assay-search-clear"
                        onClick={clearSearch}
                        aria-label="Clear"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>

                  {showDropdown && (
                    <div className="assay-dropdown">
                      {isSearching && !suggestions.length && (
                        <div className="assay-dropdown-loading">Searching…</div>
                      )}
                      {!isSearching && query.trim() && !suggestions.length && (
                        <div className="assay-dropdown-empty">No matching converters</div>
                      )}
                      {suggestions.map((item, idx) => (
                        <button
                          key={itemKey(item, idx)}
                          type="button"
                          className={`assay-dropdown-item${
                            idx === activeIndex ? " is-active" : ""
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applySearchOem(item)}
                        >
                          <span className="assay-dropdown-oem">{item.oem}</span>
                          <span className="assay-dropdown-meta">
                            {[item.description, item.type, item.manufacturer]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </OutsideClickHandler>
            </div>

            <div className="assay-filter-row" role="tablist" aria-label="Review status filter">
              {STATUS_FILTERS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === opt.value}
                  className={`assay-filter-chip${statusFilter === opt.value ? " is-selected" : ""}`}
                  onClick={() => onFilterChange(opt.value)}
                >
                  {opt.label}
                  <span className="assay-filter-count">
                    {listTotals[opt.value] != null ? listTotals[opt.value] : "—"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="assay-list-section">
            <div className="assay-table-wrap">
              {isListLoading && <div className="assay-list-empty">Loading OEMs…</div>}

              {!isListLoading && !oemList.length && (
                <div className="assay-list-empty">
                  {listQuery
                    ? `No converters matching “${listQuery}”`
                    : "No converters in this filter"}
                </div>
              )}

              {!isListLoading && oemList.length > 0 && (
                <div className="assay-table">
                  <div className="assay-table-head">
                    <span>OEM</span>
                    <span>Assay $</span>
                    <span>Override</span>
                    <span>Pt</span>
                    <span>Pd</span>
                    <span>Rh</span>
                    <span>Wt</span>
                    <span>Type</span>
                    <span>Feedback</span>
                    <span></span>
                  </div>
                  <div className="assay-table-body">
                    {oemList.map((item, idx) => {
                      const key = rowId(item);
                      const draft = rowFeedback[key] || feedbackOf(item);
                      const isSaving = savingKey === key;
                      const dirty = draft !== feedbackOf(item);

                      return (
                        <div
                          key={itemKey(item, idx)}
                          className={`assay-table-row${dirty ? " is-dirty" : ""}`}
                        >
                          <div className="assay-td-oem">
                            <div className="assay-td-oem-name">{item.oem || "—"}</div>
                            <div className="assay-td-oem-meta">{item.description || "—"}</div>
                          </div>
                          <div className="assay-td-num">{money(item.assayPrice)}</div>
                          <div className="assay-td-num">{money(item.price)}</div>
                          <div className="assay-td-num">{num(item.ptAssay)}</div>
                          <div className="assay-td-num">{num(item.pdAssay)}</div>
                          <div className="assay-td-num">{num(item.rhAssay)}</div>
                          <div className="assay-td-weight">
                            {editingWeightKey === key ? (
                              <>
                                <input
                                  type="number"
                                  step="any"
                                  min="0"
                                  className="assay-weight-input"
                                  value={weightDraft}
                                  autoFocus
                                  disabled={!!savingWeightKey}
                                  onChange={(e) => setWeightDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      saveWeight(item);
                                    } else if (e.key === "Escape") {
                                      e.preventDefault();
                                      cancelEditWeight();
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  className="assay-weight-save"
                                  disabled={!!savingWeightKey}
                                  onClick={() => saveWeight(item)}
                                >
                                  {savingWeightKey === key ? "…" : "✓"}
                                </button>
                                <button
                                  type="button"
                                  className="assay-weight-cancel"
                                  disabled={!!savingWeightKey}
                                  onClick={cancelEditWeight}
                                  aria-label="Cancel weight edit"
                                  title="Cancel"
                                >
                                  ✕
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="assay-td-num">{num(item.subweight)}</span>
                                <button
                                  type="button"
                                  className="assay-weight-edit"
                                  onClick={() => startEditWeight(item)}
                                  aria-label={`Edit weight for ${item.oem || "converter"}`}
                                  title="Edit weight"
                                >
                                  <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                                    <path
                                      fill="currentColor"
                                      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.99-1.66z"
                                    />
                                  </svg>
                                </button>
                              </>
                            )}
                          </div>
                          <div className="assay-td-muted">{item.type || "—"}</div>
                          <div className="assay-td-feedback">
                            <div
                              className="assay-feedback-chips"
                              role="group"
                              aria-label={`Price feedback for ${item.oem || "converter"}`}
                            >
                              {PRICE_OPTIONS.map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  className={`assay-fb-chip${
                                    opt.value === "rejected" ? " is-reject" : ""
                                  }${draft === opt.value ? " is-selected" : ""}`}
                                  disabled={!!savingKey}
                                  title={opt.label}
                                  onClick={() => setFeedbackForRow(item, opt.value)}
                                >
                                  {opt.short || opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="assay-td-action">
                            <button
                              type="button"
                              className="assay-row-save"
                              disabled={!draft || !!savingKey}
                              onClick={() => saveReview(item)}
                            >
                              {isSaving ? "…" : "Save"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="assay-board-footer">
              <div className="assay-list-meta">
                Showing {oemList.length}
                {listTotal ? ` of ${listTotal}` : ""}
              </div>
              {hasMore && (
                <button
                  type="button"
                  className="assay-load-more"
                  disabled={isLoadingMore}
                  onClick={() =>
                    loadList({
                      filter: statusFilter,
                      offset: oemList.length,
                      append: true,
                      text: listQuery,
                    })
                  }
                >
                  {isLoadingMore ? "Loading…" : `Load ${PAGE_SIZE} more`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {toastParams.message && (
        <Toast type={toastParams.type} message={toastParams.message} id={toastParams.id} />
      )}
    </PageLayout>
  );
};

export default AssayInsights;
